import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { ChevronRight, Clock, Megaphone, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import {
  useCreateChatGroupMutation,
  useCreateChatWorkStatusBroadcastMutation,
  useGetChatCapabilitiesQuery,
  useGetChatWorkStatusRecipientsQuery,
  useOpenChatDirectMutation,
} from "@/services/(my-space)/api.chat";
import { getErrorMessage } from "@/utils/error.utils";
import { cn } from "@/utils/cn";
import {
  CHAT_WORK_STATUSES,
  type ChatWorkStatus,
} from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { ContactList } from "./ContactList";
import { MemberSelector } from "./MemberSelector";
import {
  chatWorkStatusOption,
  personName,
  selfRowMatchesSearch,
} from "../_lib/chat-labels";
import {
  MAX_CHAT_MEMBERS,
  emptySelection,
  type PeopleSelection,
} from "../_lib/audience-selection";

type Step = "menu" | "group" | "broadcast" | "work-status";

/** Where "Create new" opens: its menu, or straight on one of its steps. */
export type NewChatStart = Step;

interface NewChatDialogProps {
  open: boolean;
  onClose: () => void;
  onOpened: (conversationId: number) => void;
  /** Default: the menu. "group" opens on the new-group form (Back still leads to the menu). */
  start?: NewChatStart;
}

/**
 * "Create new": group, broadcast, message by work status, or a colleague.
 * The title and search stay put; the actions and contacts scroll together.
 */
export const NewChatDialog = ({ open, onClose, onOpened, start = "menu" }: NewChatDialogProps) => {
  const { t } = useTranslation("chat");
  const [step, setStep] = useState<Step>("menu");
  const { data: capabilities } = useGetChatCapabilitiesQuery(undefined, { skip: !open });
  const [openDirect, { isLoading: isOpening }] = useOpenChatDirectMutation();
  const me = useAppSelector(selectUser).user;
  const myUserId = me?.id;

  useEffect(() => {
    if (open) setStep(start);
  }, [open, start]);

  const done = (id: number) => {
    onClose();
    onOpened(id);
  };

  /** A colleague, or my own id for "Message yourself" (the same get-or-create). */
  const startDirect = async (userId: number) => {
    if (isOpening) return;
    try {
      const conversation = await openDirect(userId).unwrap();
      done(conversation.id);
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.open")));
    }
  };

  const canBroadcast = !!capabilities?.canBroadcast;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* Roomy: the group step stacks a name, the selection, "Add by" and a list. */}
      <DialogContent className="flex h-[min(760px,90vh)] flex-col gap-5 sm:max-w-xl sm:p-7">
        {step === "menu" ? (
          <>
            <DialogHeader>
              <DialogTitle>{t("new.title")}</DialogTitle>
            </DialogHeader>
            <ContactList
              className="flex-1"
              excludeIds={myUserId != null ? [myUserId] : []}
              onPick={(c) => void startDirect(c.id)}
              header={(search) => (
                <div className="flex flex-col gap-1.5 pb-2">
                  <MenuRow
                    icon={<Users className="size-5" />}
                    label={t("new.group")}
                    onClick={() => setStep("group")}
                  />
                  {canBroadcast ? (
                    <>
                      <MenuRow
                        icon={<Megaphone className="size-5" />}
                        label={t("new.broadcast")}
                        onClick={() => setStep("broadcast")}
                      />
                      <MenuRow
                        icon={<Clock className="size-5" />}
                        label={t("new.by_work_status")}
                        badge={t("new.new_badge")}
                        onClick={() => setStep("work-status")}
                      />
                    </>
                  ) : null}
                  <div className="px-3 pt-5 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                    {t("new.contacts")}
                  </div>
                  {me && selfRowMatchesSearch(t, me, search) ? (
                    <SelfRow
                      me={me}
                      disabled={isOpening}
                      onClick={() => void startDirect(me.id)}
                    />
                  ) : null}
                </div>
              )}
            />
          </>
        ) : step === "work-status" ? (
          <WorkStatusStep onBack={() => setStep("menu")} onDone={done} />
        ) : (
          <CreateGroupStep kind={step} onBack={() => setStep("menu")} onDone={done} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const MenuRow = ({
  icon,
  label,
  badge,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-4 rounded-md px-3 py-3 text-start hover:bg-muted"
  >
    <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary-muted text-primary">
      {icon}
    </span>
    <span className="flex-1 text-[15px] font-medium text-foreground">{label}</span>
    {badge ? <Badge variant="primary">{badge}</Badge> : null}
    <ChevronRight className="size-4 text-muted-foreground rtl-flip" />
  </button>
);

/** "Message yourself": my own notes chat, first among the contacts. */
const SelfRow = ({
  me,
  disabled,
  onClick,
}: {
  me: { firstName: string; lastName: string; profileImage: string | null };
  disabled: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation("chat");
  const name = personName(t, me);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-4 rounded-md px-3 py-2.5 text-start hover:bg-muted disabled:opacity-60"
    >
      <ChatAvatar name={name} imageUrl={me.profileImage} size="md" />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-foreground">
          {t("labels.self_title", { name })}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {t("new.message_yourself_hint")}
        </span>
      </div>
    </button>
  );
};

const StepHeader = ({ title, onBack }: { title: string; onBack: () => void }) => {
  const { t } = useTranslation("chat");
  return (
    <DialogHeader className="flex-row items-center gap-2">
      <Button variant="ghost" size="icon-sm" onClick={onBack} aria-label={t("new.back")}>
        <ChevronRight className="size-4 rotate-180 rtl:rotate-0" />
      </Button>
      <DialogTitle>{title}</DialogTitle>
    </DialogHeader>
  );
};

const CreateGroupStep = ({
  kind,
  onBack,
  onDone,
}: {
  kind: "group" | "broadcast";
  onBack: () => void;
  onDone: (id: number) => void;
}) => {
  const { t } = useTranslation("chat");
  const myUserId = useAppSelector(selectUser).user?.id;
  const [name, setName] = useState("");
  const [selection, setSelection] = useState<PeopleSelection>(emptySelection);
  const picked = selection.people;
  const [showErrors, setShowErrors] = useState(false);
  const [create, { isLoading }] = useCreateChatGroupMutation();
  const excludeIds = useMemo(() => (myUserId != null ? [myUserId] : []), [myUserId]);

  const nameError = !name.trim() ? t("new.name_required") : null;
  const membersError = !picked.length
    ? t("new.members_required")
    : picked.length > MAX_CHAT_MEMBERS
      ? t("new.members_max", { max: MAX_CHAT_MEMBERS })
      : null;

  const submit = async () => {
    if (nameError || membersError) {
      setShowErrors(true);
      return;
    }
    try {
      const conversation = await create({
        kind,
        name: name.trim(),
        memberIds: picked.map((p) => p.id),
      }).unwrap();
      onDone(conversation.id);
    } catch (e) {
      toast.error(
        getErrorMessage(e, kind === "group" ? t("errors.create_group") : t("errors.create_broadcast")),
      );
    }
  };

  return (
    <>
      <StepHeader title={kind === "group" ? t("new.group") : t("new.broadcast")} onBack={onBack} />
      <div className="flex shrink-0 flex-col gap-2">
        <Label htmlFor="chat-group-name">
          {kind === "group" ? t("new.group_name") : t("new.broadcast_name")}
        </Label>
        <Input
          id="chat-group-name"
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          aria-invalid={showErrors && !!nameError}
          className="h-10"
        />
        {showErrors && nameError ? <p className="text-xs text-danger">{nameError}</p> : null}
        <p className="text-xs text-muted-foreground">
          {kind === "group" ? t("new.group_hint") : t("new.broadcast_hint")}
        </p>
      </div>
      <MemberSelector
        className="flex-1"
        selection={selection}
        setSelection={setSelection}
        excludeIds={excludeIds}
        error={
          (showErrors || picked.length > MAX_CHAT_MEMBERS) && membersError ? (
            <p className="text-xs text-danger">{membersError}</p>
          ) : null
        }
      />
      <DialogFooter className="items-center sm:justify-between">
        <span className="text-xs text-muted-foreground">
          {t("new.selected", { count: picked.length })}
        </span>
        <Button variant="primary" onClick={() => void submit()} isLoading={isLoading}>
          {kind === "group" ? t("new.create_group") : t("new.create_broadcast")}
        </Button>
      </DialogFooter>
    </>
  );
};

const PREVIEW_AVATARS = 6;

const WorkStatusStep = ({
  onBack,
  onDone,
}: {
  onBack: () => void;
  onDone: (id: number) => void;
}) => {
  const { t } = useTranslation("chat");
  const [workStatus, setWorkStatus] = useState<ChatWorkStatus>("clocked_in");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const recipients = useGetChatWorkStatusRecipientsQuery(workStatus);
  const [send, { isLoading }] = useCreateChatWorkStatusBroadcastMutation();
  const count = recipients.data?.count ?? 0;

  const submit = async () => {
    if (!message.trim()) {
      setShowErrors(true);
      return;
    }
    try {
      const conversation = await send({
        workStatus,
        message: message.trim(),
        name: name.trim() || undefined,
      }).unwrap();
      toast.success(t("new.work_status_sent", { count: Math.max(conversation.memberCount - 1, 0) }));
      onDone(conversation.id);
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.send_work_status")));
    }
  };

  return (
    <>
      <StepHeader title={t("new.by_work_status")} onBack={onBack} />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <DialogDescription>{t("new.work_status_intro")}</DialogDescription>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup">
          {CHAT_WORK_STATUSES.map((ws) => (
            <button
              key={ws}
              type="button"
              role="radio"
              aria-checked={workStatus === ws}
              onClick={() => setWorkStatus(ws)}
              className={cn(
                "rounded-md border px-3 py-2 text-sm font-medium transition-colors",
                workStatus === ws
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-foreground hover:bg-muted",
              )}
            >
              {chatWorkStatusOption(t, ws)}
            </button>
          ))}
        </div>

        <div className="rounded-md border border-border bg-muted/40 p-3">
          {recipients.isFetching ? (
            <Skeleton className="h-8 w-2/3" />
          ) : count === 0 ? (
            <p className="text-sm text-muted-foreground">{t("new.work_status_nobody")}</p>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2 rtl:space-x-reverse">
                  {recipients.data!.preview.slice(0, PREVIEW_AVATARS).map((p) => (
                    <ChatAvatar
                      key={p.id}
                      name={personName(t, p)}
                      imageUrl={p.profileImage}
                      size="sm"
                      className="rounded-full ring-2 ring-background"
                    />
                  ))}
                </div>
                {count > PREVIEW_AVATARS ? (
                  <span className="text-xs text-muted-foreground">
                    {t("new.and_more", { count: count - PREVIEW_AVATARS })}
                  </span>
                ) : null}
              </div>
              <p className="text-sm font-medium text-foreground">
                {t("new.work_status_recipients", { count })}
              </p>
              <p className="text-xs text-muted-foreground">{t("new.work_status_snapshot")}</p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chat-ws-name">{t("new.name_optional")}</Label>
          <Input
            id="chat-ws-name"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            className="h-10"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="chat-ws-message">{t("new.message")}</Label>
          <Textarea
            id="chat-ws-message"
            value={message}
            maxLength={4000}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("new.message_placeholder")}
            aria-invalid={showErrors && !message.trim()}
            className="min-h-24"
          />
          {showErrors && !message.trim() ? (
            <p className="text-xs text-danger">{t("new.message_required")}</p>
          ) : null}
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="primary"
          onClick={() => void submit()}
          isLoading={isLoading}
          disabled={count === 0 || recipients.isFetching}
        >
          {t("new.send")}
        </Button>
      </DialogFooter>
    </>
  );
};
